<script setup lang="ts">
import { computed, Ref, ref } from "vue";
import { formatNumber } from "chartjs-graph";
import { useSegmentBuilderStore } from "@/modules/segments/store";
import { downloadSegmentData } from "@/modules/segments/components/DownloadSegment";
import NewSegmentScheduleDialog from "@/modules/segments/components/NewSegmentScheduleDialog.vue";
import { useBaseStore } from "@/stores/base";
import dayjs from "dayjs";
import Switch from "@/ui/components/switch/switch.vue";
import IntegrationWizard from "@/modules/integrations/components/ConfigureIntegrationExecution/IntegrationWizard.vue";

const segmentBuilderState = useSegmentBuilderStore().segmentBuilderState;

const segmentSize = computed(() => {
	return (
		segmentBuilderState.introspectResult[
			segmentBuilderState.introspectResult.length - 1
		]?.count ?? 0
	);
});

const segmentSizeRelativeToBase = computed(() => {
	return (segmentSize.value / segmentBuilderState.totalSegmentBase) * 100;
});

const isLoadingSegmentSize = computed(() => {
	return segmentBuilderState.introspectResult.length === 0;
});

const isNewSegmentScheduleDialogVisible: Ref<boolean> = ref(false);
const selectedDate = ref(new Date());
const isExportDialogVisible: Ref<boolean> = ref(false);
const isIntegrationWizardVisible: Ref<boolean> = ref(false);
const useSpecificDate: Ref<boolean> = ref(false);
const exportFormat = ref<"csv" | "xlsx">("csv");

const createExportSchedule = () => {
	if (segmentBuilderState.segment?.id) {
		isNewSegmentScheduleDialogVisible.value = true;
	}
};

const downloadData = () => {
	if (useSpecificDate.value && selectedDate.value) {
		const formattedDate = dayjs(selectedDate.value).format("YYYY-MM-DD");
		downloadSegmentData(
			segmentBuilderState.segment,
			exportFormat.value,
			formattedDate
		);
	} else {
		downloadSegmentData(segmentBuilderState.segment, exportFormat.value);
	}
	isExportDialogVisible.value = false;
};

const userCanScheduleExport = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_manage_scheduler"
	);
});

const userCanExportToIntegration = computed(() => {
	return useBaseStore().currentUser?.derivedPermissions.includes(
		"can_manage_integrations"
	);
});

const showExportTroughIntegration = computed(
	() =>
		userCanExportToIntegration.value &&
		segmentBuilderState.segment?.type === "reservation"
);
</script>

<template>
	<integration-wizard
		v-if="isIntegrationWizardVisible"
		v-model="isIntegrationWizardVisible"
		availability="available_on_reservation_segments"
		type="segment"
		:source-data="segmentBuilderState.segment"
	/>
	<new-segment-schedule-dialog
		v-if="isNewSegmentScheduleDialogVisible"
		v-model="isNewSegmentScheduleDialogVisible"
		:segment_uuid="segmentBuilderState.segment.uuid"
	/>

	<v-dialog v-model="isExportDialogVisible" max-width="600px">
		<v-card rounded elevation="2" class="export-dialog">
			<v-toolbar>
				<v-toolbar-title>Export segment data</v-toolbar-title>
				<v-spacer></v-spacer>

				<v-btn icon @click="isExportDialogVisible = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-divider />
			<v-card-text class="pa-6">
				<!-- Toggle section -->
				<div class="export-toggle-section">
					<p class="text-body-2 text-medium-emphasis mb-2">
						Export data using either the current state or from a specific date
						in the past
					</p>
					<Switch
						v-model="useSpecificDate"
						label="Specific date"
						label-position="right"
					/>
				</div>

				<v-expand-transition>
					<div v-if="useSpecificDate" class="date-picker-section">
						<v-divider class="my-2" />
						<vc-date-picker
							style="width: 100%"
							v-model="selectedDate"
							mode="date"
							class="export-date-picker"
						/>
						<p
							class="text-caption text-medium-emphasis mt-2"
							v-if="selectedDate"
						>
							Data will be exported as of
							{{ dayjs(selectedDate).format("MMMM D, YYYY") }}
						</p>
					</div>
				</v-expand-transition>

				<!-- Format selection -->
				<div class="format-selection-section mt-4">
					<v-divider class="my-2" />
					<span class="text-subtitle-2 mb-1">Export format</span>
					<v-radio-group v-model="exportFormat" inline hide-details>
						<v-radio value="csv" label="CSV"></v-radio>
						<v-radio value="xlsx" label="Excel (XLSX)"></v-radio>
					</v-radio-group>
					<p class="text-caption text-medium-emphasis mt-1">
						Excel format may take longer to generate for large datasets.
					</p>
				</div>

				<!-- Action buttons -->
				<div class="d-flex mt-6">
					<v-spacer />
					<v-btn
						variant="text"
						color="error"
						class="mr-5"
						@click="isExportDialogVisible = false"
					>
						Cancel
					</v-btn>
					<v-btn
						variant="flat"
						:disabled="useSpecificDate && selectedDate === null"
						prepend-icon="mdi-download"
						@click="downloadData"
					>
						Export Data
					</v-btn>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>

	<v-sheet
		class="v-sheet--offset mx-auto d-flex"
		color="rgba(var(--color-secondary), .65)"
	>
		<div class="d-flex px-10 py-10" style="position: relative">
			<div style="position: absolute; top: 10px; left: 20px">
				<span class="text-caption" style="color: #dadada">S I Z E </span>
			</div>
			<div class="">
				<v-progress-circular
					:model-value="segmentSizeRelativeToBase"
					:indeterminate="
						isLoadingSegmentSize && !segmentBuilderState.introspectErrorDetected
					"
					:rotate="360"
					:size="150"
					:width="15"
					color="primary"
				>
					<template v-slot:default>
						{{
							isLoadingSegmentSize &&
							!segmentBuilderState.introspectErrorDetected
								? "Calculating..."
								: formatNumber(segmentSizeRelativeToBase, "percent") + `%`
						}}
					</template>
				</v-progress-circular>
			</div>
			<div class="py-5 pl-10" style="width: 250px">
				<div>
					<span class="font-weight-bold">Segment base:</span>
					{{ formatNumber(segmentBuilderState.totalSegmentBase, "default") }}
				</div>
				<div>
					<span class="font-weight-bold">Segment size:</span>
					{{
						isLoadingSegmentSize && !segmentBuilderState.introspectErrorDetected
							? "Calculating..."
							: formatNumber(segmentSize, "default")
					}}
				</div>
			</div>
		</div>
		<v-divider vertical />
		<div class="w-100 px-10 py-10" style="position: relative">
			<div class="d-flex top-right-menu">
				<v-spacer />
				<v-menu location="bottom right">
					<template v-slot:activator="{ props }">
						<v-btn
							v-bind="props"
							icon="fa: fa-regular fa-ellipsis-vertical"
							variant="text"
						/>
					</template>
					<v-list class="pa-1 mt-2">
						<v-list-item
							class="font-weight-regular text-caption"
							@click="isExportDialogVisible = true"
						>
							<template v-slot:prepend>
								<i class="fa fa-download mr-3"></i>
							</template>
							Export segment data
						</v-list-item>

						<v-list-item
							v-if="showExportTroughIntegration"
							class="font-weight-regular text-caption"
							@click="isIntegrationWizardVisible = true"
						>
							<template v-slot:prepend>
								<i class="fa fa-wand-sparkles mr-3"></i>
							</template>
							Export once through integration
						</v-list-item>

						<div>
							<v-list-item
								v-if="userCanScheduleExport"
								:disabled="!segmentBuilderState.segment?.id"
								class="font-weight-regular text-caption"
								@click="createExportSchedule"
							>
								<template v-slot:prepend>
									<i class="fa fa-calendar-clock mr-3"></i>
								</template>
								Schedule automated export
							</v-list-item>
							<v-tooltip
								activator="parent"
								location="bottom right"
								v-if="!segmentBuilderState.segment?.id"
							>
								You need to save this segment before scheduling an export.
							</v-tooltip>
						</div>
					</v-list>
				</v-menu>
			</div>
		</div>
	</v-sheet>
</template>

<style scoped lang="scss">
.top-right-menu {
	position: absolute;
	top: 10px;
	right: 10px;
}

.export-dialog {
	.export-toggle-section {
		border-radius: 8px;
		transition: all 0.2s ease;
	}

	.format-selection-section {
		border-radius: 8px;
		transition: all 0.2s ease;
	}
}
</style>
